import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Wrench, Flame, Droplets, Key, Shield, Phone } from "lucide-react";
import GradientButton from "./button-1";
import { GridPattern } from "./grid-pattern";

const services = [
  {
    icon: Wrench,
    title: "Plomberie",
    description: "Installation, réparation et entretien de tous vos équipements sanitaires",
    features: ["Fuites d'eau", "Installation sanitaires", "Remplacement tuyauterie", "Détection de fuites"]
  },
  {
    icon: Flame,
    title: "Chauffage",
    description: "Solutions complètes pour votre confort thermique toute l'année",
    features: ["Entretien chaudière", "Installation radiateurs", "Pompes à chaleur", "Dépannage urgence"]
  },
  {
    icon: Droplets,
    title: "Débouchage",
    description: "Intervention rapide pour tous types de canalisations bouchées",
    features: ["Débouchage WC", "Canalisations bouchées", "Nettoyage haute pression", "Inspection caméra"]
  },
  {
    icon: Key,
    title: "Serrurerie",
    description: "Service de serrurerie et dépannage d'urgence disponible 24h/24",
    features: ["Ouverture de porte", "Remplacement serrures", "Installation blindage", "Dépannage urgence"]
  }
];

export function AnimatedHero({ scrollToSection }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef(null);

  // Mouse position for magnetic effect
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springConfig = { damping: 25, stiffness: 200 };
  const x = useSpring(mouseX, springConfig);
  const y = useSpring(mouseY, springConfig);

  // Transform for parallax effects
  const numberX = useTransform(x, [-200, 200], [-15, 15]);
  const numberY = useTransform(y, [-200, 200], [-8, 8]);
  const imageX = useTransform(x, [-200, 200], [10, -10]);
  const imageY = useTransform(y, [-200, 200], [5, -5]);

  const handleMouseMove = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      mouseX.set(e.clientX - centerX);
      mouseY.set(e.clientY - centerY);
    }
  };

  const goNext = () => setActiveIndex((prev) => (prev + 1) % services.length);
  const goPrev = () => setActiveIndex((prev) => (prev - 1 + services.length) % services.length);

  useEffect(() => {
    const timer = setInterval(goNext, 5000);
    return () => clearInterval(timer);
  }, []);

  const current = services[activeIndex];
  const IconComponent = current.icon;

  return (
    <section className="min-h-screen flex items-center justify-center bg-white pt-32 relative overflow-hidden">
      {/* Grid Pattern Background */}
      <GridPattern
        width={60}
        height={60}
        x={-1}
        y={-1}
        strokeDasharray="0"
        className="fill-blue-50/50 stroke-blue-100/50 [mask-image:radial-gradient(800px_circle_at_center,white,transparent)]"
      />
      
      <div 
        ref={containerRef} 
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10"
        onMouseMove={handleMouseMove}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left order-2 lg:order-1">
            {/* Animated Service Badge */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.4 }}
                className="inline-flex items-center gap-3 border border-blue-900 text-blue-900 px-6 py-3 rounded-full text-sm font-medium mb-8"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg flex items-center justify-center">
                  <IconComponent className="w-4 h-4 text-blue-600" />
                </div>
                <span>{current.title} • Disponible 24/7</span>
              </motion.div>
            </AnimatePresence>

            {/* Main Title with Service Integration */}
            <motion.h1 
              className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-blue-900 mb-8 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Services Professionnels de{" "}
              <AnimatePresence mode="wait">
                <motion.span
                  key={activeIndex}
                  initial={{ opacity: 0, y: 20, rotateX: 90 }}
                  animate={{ opacity: 1, y: 0, rotateX: 0 }}
                  exit={{ opacity: 0, y: -20, rotateX: -90 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="inline-block text-orange-500"
                >
                  {current.title}
                </motion.span>
              </AnimatePresence>
              {" "}en Belgique
            </motion.h1>

            {/* Animated Service Description */}
            <AnimatePresence mode="wait">
              <motion.p
                key={activeIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed"
              >
                {current.description}
              </motion.p>
            </AnimatePresence>

            {/* Service Features */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                className="grid grid-cols-2 gap-3 mb-12 text-sm"
              >
                {current.features.map((feature, index) => (
                  <motion.div
                    key={feature}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
                    className="flex items-center space-x-2 text-gray-600"
                  >
                    <div className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0" />
                    <span>{feature}</span>
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>

            {/* CTA Buttons */}
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <GradientButton 
                variant="primary"
                width="280px"
                height="60px"
                onClick={() => window.location.href = 'tel:0465195726'}
                className="shadow-lg hover:shadow-xl"
              >
                <Phone className="w-5 h-5 mr-2" />
                Appeler Maintenant
              </GradientButton>
              <GradientButton 
                variant="secondary"
                width="280px"
                height="60px"
                onClick={() => scrollToSection('contact')}
                className="shadow-lg hover:shadow-xl"
              >
                Demander un Devis
              </GradientButton>
            </motion.div>

            {/* Service Navigation */}
            <div className="flex items-center justify-center lg:justify-start gap-4">
              {/* Service indicators */}
              <div className="flex items-center gap-2">
                {services.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveIndex(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === activeIndex 
                        ? 'bg-blue-900 scale-125' 
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>

              {/* Navigation arrows */}
              <div className="flex items-center gap-2 ml-4">
                <motion.button
                  onClick={goPrev}
                  className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center hover:border-blue-900 transition-colors"
                  whileTap={{ scale: 0.95 }}
                >
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" className="text-blue-900">
                    <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </motion.button>
                <motion.button
                  onClick={goNext}
                  className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center hover:border-blue-900 transition-colors"
                  whileTap={{ scale: 0.95 }}
                >
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" className="text-blue-900">
                    <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </motion.button>
              </div>
            </div>
          </div>

          {/* Right Image with Parallax */}
          <div className="relative order-1 lg:order-2">
            {/* Oversized service number */}
            <motion.div
              className="absolute -top-8 -left-8 text-[12rem] md:text-[16rem] font-bold text-blue-900/[0.03] select-none pointer-events-none leading-none tracking-tighter z-0"
              style={{ x: numberX, y: numberY }}
            >
              <AnimatePresence mode="wait">
                <motion.span
                  key={activeIndex}
                  initial={{ opacity: 0, scale: 0.8, filter: "blur(10px)" }}
                  animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                  exit={{ opacity: 0, scale: 1.1, filter: "blur(10px)" }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className="block"
                >
                  {String(activeIndex + 1).padStart(2, "0")}
                </motion.span>
              </AnimatePresence>
            </motion.div>

            {/* Hero Image with Parallax */}
            <motion.div 
              className="relative overflow-hidden rounded-2xl shadow-2xl z-10"
              style={{ x: imageX, y: imageY }}
            >
              <img
                src="/plumber-hero.png"
                alt="Technicien professionnel Esiteck spécialisé en plomberie, chauffage, débouchage et serrurerie"
                className="w-full h-96 lg:h-[600px] object-cover"
                loading="eager"
              />
              
              {/* Overlay with Trust Badge */}
              <div className="absolute bottom-6 left-6 bg-white/95 backdrop-blur-md rounded-xl p-4 shadow-xl">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl flex items-center justify-center">
                    <Shield className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-bold text-blue-900">Certifié & Assuré</div>
                    <div className="text-sm text-gray-600">Plus de 10 ans d'expérience</div>
                    <div className="flex items-center space-x-1 mt-1">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="w-3 h-3 bg-yellow-400 rounded-full" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom ticker - service names */}
        <div className="absolute bottom-8 left-0 right-0 overflow-hidden opacity-20 pointer-events-none">
          <motion.div
            className="flex whitespace-nowrap text-2xl md:text-4xl font-bold tracking-tight text-blue-900"
            animate={{ x: [0, -1000] }}
            transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          >
            {[...Array(6)].map((_, i) => (
              <span key={i} className="mx-12">
                {services.map((s) => s.title).join(" • ")} •
              </span>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}